import { getEventTypeDef } from "./calendarRoles.js";
import { computeCeremonyTiming, getCeremonyPlaceById, getCeremonyPlaceLabel } from "./ceremonyPlaces.js";

function fullName(first, last) {
  return [first, last].filter(Boolean).join(" ").trim();
}

function safeArray(arr) {
  return Array.isArray(arr) ? arr.filter(Boolean) : [];
}

function toIsoMaybe(value) {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function getObradTimingConfig(obrad = {}) {
  const mistoId = String(obrad?.mistoId || "").trim();
  const mistoNazev =
    String(obrad?.misto || obrad?.krematorium || obrad?.hrbitov || "").trim();

  const resolvedPlace =
    getCeremonyPlaceById(mistoId) ||
    getCeremonyPlaceById(mistoNazev);

  const placeId = resolvedPlace?.id || mistoId || mistoNazev || "";

  return computeCeremonyTiming({
    placeId,
    ceremonyVariant: obrad?.ceremonyVariant || obrad?.variantaRozlouceni || "",
    churchServiceType: obrad?.churchServiceType || obrad?.typRozlouceniVKostele || "",
  });
}

export function buildCalendarEvent(input = {}) {
  const typUdalosti = input.typUdalosti || "interni_blokace";
  const typeDef = getEventTypeDef(typUdalosti);

  return {
    id: input.id || "",
    orderId: input.orderId || input.zakazkaId || "",
    typUdalosti,
    stav: input.stav || "navrh",
    isTentative: Boolean(input.isTentative ?? (input.stav === "navrh")),
    start: toIsoMaybe(input.start || input.zacatek),
    end: toIsoMaybe(input.end || input.konec),
    bufferBeforeMin: Number.isFinite(input.bufferBeforeMin)
      ? input.bufferBeforeMin
      : typeDef.bufferBeforeMin,
    bufferAfterMin: Number.isFinite(input.bufferAfterMin)
      ? input.bufferAfterMin
      : typeDef.bufferAfterMin,
    mistoNazev: input.mistoNazev || "",
    mistoAdresa: input.mistoAdresa || "",
    mistoTyp: input.mistoTyp || "",
    objednavatel: input.objednavatel || "",
    zesnuly: input.zesnuly || "",
    poznamka: input.poznamka || "",
    source: input.source || "manual",
    roleAssignments: safeArray(input.roleAssignments).map((x) => ({
      role: x.role || "",
      personId: x.personId || "",
      personLabel: x.personLabel || x.personName || x.personId || "",
    })),
    vehicleAssignments: safeArray(input.vehicleAssignments).map((x) => ({
      role: x.role || "",
      vehicleId: x.vehicleId || "",
      vehicleLabel: x.vehicleLabel || x.vehicleId || "",
    })),
  };
}

export function buildObradEventFromOrder(order = {}) {
  const obrad = order?.obrad || {};
  const zesnuly = order?.zesnulaOsoba || {};
  const objednavatel = order?.vypravitel || {};

  if (!obrad?.datum || !obrad?.cas) return null;

  const start = new Date(`${obrad.datum}T${obrad.cas}:00`);
  if (Number.isNaN(start.getTime())) return null;

  const timing = getObradTimingConfig(obrad);
  const end = new Date(start.getTime() + timing.durationMin * 60000);

  return buildCalendarEvent({
    id: `obrad_${order.id || order._id || cryptoRandomFallback()}`,
    orderId: order.id || order._id || "",
    typUdalosti: "obrad",
    stav: order.stavKalendar || "potvrzeno",
    isTentative: false,
    start,
    end,
    bufferBeforeMin: timing.bufferBeforeMin,
    bufferAfterMin: timing.bufferAfterMin,
    mistoNazev:
      getCeremonyPlaceLabel(obrad.mistoId) ||
      obrad.misto ||
      obrad.krematorium ||
      obrad.hrbitov ||
      "",
    mistoAdresa: obrad.mistoAdresa || obrad.krematoriumAdresa || obrad.hrbitovAdresa || "",
    mistoTyp: "obrad",
    objednavatel:
      objednavatel.jmenoPrijmeni ||
      fullName(objednavatel.jmeno, objednavatel.prijmeni) ||
      "",
    zesnuly:
      zesnuly.jmenoPrijmeni ||
      fullName(zesnuly.jmeno, zesnuly.prijmeni) ||
      "",
    poznamka: obrad.poznamka || "",
    source: "from_obrad",
    roleAssignments: safeArray(obrad.roleAssignments),
    vehicleAssignments: safeArray(obrad.vehicleAssignments),
  });
}

export function buildPrevozEventFromOrder(order = {}) {
  const prevoz = order?.prevoz || {};
  const zesnuly = order?.zesnulaOsoba || {};
  const objednavatel = order?.vypravitel || {};

  if (!prevoz?.datumPrevozu || !prevoz?.casPrevozu) return null;

  const start = new Date(`${prevoz.datumPrevozu}T${prevoz.casPrevozu}:00`);
  if (Number.isNaN(start.getTime())) return null;

  const defaultDurationMin = Number(prevoz.trvaniMin || 60);
  const end = new Date(start.getTime() + defaultDurationMin * 60000);

  return buildCalendarEvent({
    id: `prevoz_${order.id || order._id || cryptoRandomFallback()}`,
    orderId: order.id || order._id || "",
    typUdalosti: "prevoz",
    stav: order.stavKalendar || "potvrzeno",
    isTentative: false,
    start,
    end,
    mistoNazev: prevoz.cilPrevozuNazev || prevoz.mistoOdvozu || prevoz.typPrevozu || "",
    mistoAdresa: prevoz.cilPrevozuAdresa || prevoz.adresaOdvozu || "",
    mistoTyp: "prevoz",
    objednavatel:
      objednavatel.jmenoPrijmeni ||
      fullName(objednavatel.jmeno, objednavatel.prijmeni) ||
      "",
    zesnuly:
      zesnuly.jmenoPrijmeni ||
      fullName(zesnuly.jmeno, zesnuly.prijmeni) ||
      "",
    poznamka: prevoz.poznamka || "",
    source: "from_prevoz",
    roleAssignments: safeArray(prevoz.roleAssignments),
    vehicleAssignments: safeArray(prevoz.vehicleAssignments),
  });
}

export function buildCalendarEventsFromOrder(order = {}) {
  return [
    buildPrevozEventFromOrder(order),
    buildObradEventFromOrder(order),
  ].filter(Boolean);
}

function cryptoRandomFallback() {
  return Math.random().toString(36).slice(2, 10);
}
